<template>
  <div class="notification-badge-container">
    <div class="notification-icon" @click="toggleNotifications">
      <i class="pi pi-bell" :class="{ 'bell-animated': hasUrgent }" />
      
      <!-- Badge compteur -->
      <Badge 
        v-if="unreadCount > 0"
        :value="displayCount"
        :severity="hasUrgent ? 'danger' : 'info'"
        class="notification-count"
      />
      
      <!-- Indicateur de connexion -->
      <div 
        class="connection-indicator"
        :class="{ 
          'connected': isConnected, 
          'disconnected': !isConnected 
        }"
        :title="isConnected ? 'Notifications en temps réel actives' : 'Connexion notifications interrompue'"
      />
    </div>

    <!-- Dropdown notifications rapides -->
    <div 
      v-if="showQuickPreview" 
      class="quick-notifications-dropdown"
      @click.stop
    >
      <div class="dropdown-header">
        <h4>Notifications</h4>
        <div class="header-actions">
          <Button 
            icon="pi pi-check-circle"
            @click="markAllAsRead"
            :disabled="unreadCount === 0"
            text
            size="small"
            title="Tout marquer comme lu"
          />
          <Button 
            icon="pi pi-external-link"
            @click="openNotificationCenter"
            text
            size="small"
            title="Ouvrir le centre de notifications"
          />
          <Button 
            icon="pi pi-times"
            @click="closeQuickPreview"
            text
            size="small"
          />
        </div>
      </div>

      <div class="notifications-list">
        <!-- Notifications urgentes en premier -->
        <div v-if="urgentNotifications.length > 0" class="urgent-section">
          <h5><i class="pi pi-exclamation-triangle" /> Urgent</h5>
          <div 
            v-for="notification in urgentNotifications.slice(0, 3)"
            :key="notification.id"
            class="notification-item urgent"
            @click="selectNotification(notification)"
          >
            <div class="notification-content">
              <div class="notification-title">{{ notification.title }}</div>
              <div class="notification-message">{{ notification.message }}</div>
              <div class="notification-time">{{ formatTime(notification.created_at) }}</div>
            </div>
            <div class="notification-actions">
              <Button 
                icon="pi pi-check"
                @click.stop="markAsRead(notification.id)"
                text
                size="small"
                title="Marquer comme lu"
              />
            </div>
          </div>
        </div>

        <!-- Autres notifications récentes -->
        <div v-if="recentNotifications.length > 0" class="recent-section">
          <h5 v-if="urgentNotifications.length > 0">
            <i class="pi pi-clock" /> Récentes
          </h5>
          <div 
            v-for="notification in recentNotifications.slice(0, showLimit)"
            :key="notification.id"
            class="notification-item"
            :class="{ 'unread': !notification.is_read }"
            @click="selectNotification(notification)"
          >
            <div class="notification-icon-type">
              <i :class="getNotificationIcon(notification.type)" />
            </div>
            <div class="notification-content">
              <div class="notification-title">{{ notification.title }}</div>
              <div class="notification-message">{{ notification.message }}</div>
              <div class="notification-time">{{ formatTime(notification.created_at) }}</div>
            </div>
            <div class="notification-actions">
              <Button 
                v-if="!notification.is_read"
                icon="pi pi-check"
                @click.stop="markAsRead(notification.id)"
                text
                size="small"
                title="Marquer comme lu"
              />
            </div>
          </div>
        </div>

        <!-- État vide -->
        <div v-if="notifications.length === 0" class="empty-state">
          <i class="pi pi-bell-slash" />
          <p>Aucune notification</p>
        </div>

        <!-- Bouton voir plus -->
        <div v-if="notifications.length > showLimit" class="view-more">
          <Button 
            :label="`Voir ${notifications.length - showLimit} de plus`"
            @click="openNotificationCenter"
            outlined
            size="small"
          />
        </div>
      </div>
    </div>

    <!-- Overlay pour fermer le dropdown -->
    <div 
      v-if="showQuickPreview"
      class="dropdown-overlay"
      @click="closeQuickPreview"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useNotificationStore } from '@/stores/notifications'
import Button from 'primevue/button'
import Badge from 'primevue/badge'
import type { Notification } from '@/services/notificationService'

// Props
interface Props {
  showQuickPreviewOnClick?: boolean
  maxDisplayCount?: number
  showConnectionStatus?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  showQuickPreviewOnClick: true,
  maxDisplayCount: 99,
  showConnectionStatus: true
})

// Store
const notificationStore = useNotificationStore()

// État local
const showQuickPreview = ref(false)
const showLimit = ref(5)

// Computed
const notifications = computed(() => notificationStore.notifications)
const unreadCount = computed(() => notificationStore.unreadCount)
const urgentCount = computed(() => notificationStore.urgentCount)
const isConnected = computed(() => notificationStore.isConnected)

const urgentNotifications = computed(() => 
  notifications.value.filter(n => !n.is_read && n.is_urgent)
)

const recentNotifications = computed(() => 
  notifications.value.filter(n => !n.is_urgent).slice(0, 10)
)

const hasUrgent = computed(() => urgentCount.value > 0)

const displayCount = computed(() => {
  if (unreadCount.value > props.maxDisplayCount) {
    return `${props.maxDisplayCount}+`
  }
  return unreadCount.value.toString()
})

// Méthodes
const toggleNotifications = () => {
  if (props.showQuickPreviewOnClick) {
    showQuickPreview.value = !showQuickPreview.value
  } else {
    openNotificationCenter()
  }
}

const closeQuickPreview = () => {
  showQuickPreview.value = false
}

const openNotificationCenter = () => {
  showQuickPreview.value = false
  notificationStore.openNotificationCenter()
}

const selectNotification = (notification: Notification) => {
  notificationStore.selectNotification(notification)
  showQuickPreview.value = false
  openNotificationCenter()
}

const markAsRead = async (notificationId: string) => {
  try {
    await notificationStore.markAsRead(notificationId)
  } catch (error) {
    console.error('Erreur marquage notification:', error)
  }
}

const markAllAsRead = async () => {
  try {
    await notificationStore.markAllAsRead()
    showQuickPreview.value = false
  } catch (error) {
    console.error('Erreur marquage toutes notifications:', error)
  }
}

const formatTime = (dateString: string) => {
  const date = new Date(dateString)
  const now = new Date()
  const diffMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60))
  
  if (diffMinutes < 1) return 'À l\'instant'
  if (diffMinutes < 60) return `Il y a ${diffMinutes} min`
  
  const diffHours = Math.floor(diffMinutes / 60)
  if (diffHours < 24) return `Il y a ${diffHours}h`
  
  const diffDays = Math.floor(diffHours / 24)
  if (diffDays < 7) return `Il y a ${diffDays}j`
  
  return date.toLocaleDateString('fr-FR')
}

const getNotificationIcon = (type: string) => {
  const icons: { [key: string]: string } = {
    'rdv': 'pi pi-calendar',
    'urgence': 'pi pi-exclamation-triangle',
    'planning': 'pi pi-calendar-plus',
    'system': 'pi pi-cog',
    'reminder': 'pi pi-clock',
    'document': 'pi pi-file',
    'cr_refuse': 'pi pi-replay',
    'cr_valide': 'pi pi-check-circle',
    'cr_en_attente': 'pi pi-hourglass',
    'nouvel_examen': 'pi pi-plus-circle',
    'examen_urgent': 'pi pi-exclamation-triangle',
    'examen_realise': 'pi pi-images',
    'retard_interpretation': 'pi pi-clock',
    'retard_validation': 'pi pi-clock',
    'equipement_panne': 'pi pi-wrench',
    'maintenance_programmee': 'pi pi-wrench',
  }
  return icons[type] || 'pi pi-info-circle'
}

// Gestion des clics extérieurs
const handleClickOutside = (event: Event) => {
  const container = document.querySelector('.notification-badge-container')
  if (container && !container.contains(event.target as Node)) {
    showQuickPreview.value = false
  }
}

// Lifecycle
onMounted(() => {
  document.addEventListener('click', handleClickOutside)
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
})
</script>

<style scoped>
.notification-badge-container {
  position: relative;
}

.notification-icon {
  position: relative;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 50%;
  transition: all 0.3s ease;
  color: #6b7280;
  font-size: 1.25rem;
}

.notification-icon:hover {
  background: rgba(59, 130, 246, 0.1);
  color: #3b82f6;
}

.notification-icon .pi-bell {
  transition: transform 0.3s ease;
}

.notification-icon .pi-bell.bell-animated {
  animation: bellRing 1s ease-in-out infinite;
  color: #ef4444;
}

@keyframes bellRing {
  0%, 100% { transform: rotate(0deg); }
  25% { transform: rotate(10deg); }
  75% { transform: rotate(-10deg); }
}

.notification-count {
  position: absolute;
  top: -2px;
  right: -2px;
  font-size: 0.75rem;
  min-width: 18px;
  height: 18px;
  border-radius: 9px;
}

.connection-indicator {
  position: absolute;
  bottom: 2px;
  right: 2px;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  border: 1px solid white;
}

.connection-indicator.connected {
  background: #10b981;
}

.connection-indicator.disconnected {
  background: #ef4444;
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
}

/* Dropdown notifications */
.quick-notifications-dropdown {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  width: 380px;
  max-height: 500px;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  overflow: hidden;
}

.dropdown-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  border-bottom: 1px solid #e5e7eb;
  background: #f8fafc;
}

.dropdown-header h4 {
  margin: 0;
  color: #1f2937;
  font-size: 1rem;
  font-weight: 600;
}

.header-actions {
  display: flex;
  gap: 0.25rem;
}

.notifications-list {
  max-height: 400px;
  overflow-y: auto;
}

.urgent-section,
.recent-section {
  padding: 0.5rem 0;
}

.urgent-section h5,
.recent-section h5 {
  margin: 0 0 0.5rem 0;
  padding: 0 1rem;
  font-size: 0.875rem;
  font-weight: 600;
  color: #6b7280;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.urgent-section h5 {
  color: #dc2626;
}

.notification-item {
  display: flex;
  align-items: flex-start;
  padding: 0.75rem 1rem;
  cursor: pointer;
  transition: background-color 0.2s;
  border-bottom: 1px solid #f3f4f6;
}

.notification-item:hover {
  background: #f8fafc;
}

.notification-item.unread {
  background: #eff6ff;
}

.notification-item.urgent {
  background: #fef2f2;
  border-left: 3px solid #dc2626;
}

.notification-icon-type {
  margin-right: 0.75rem;
  margin-top: 0.25rem;
  color: #6b7280;
}

.notification-content {
  flex: 1;
  min-width: 0;
}

.notification-title {
  font-weight: 600;
  color: #1f2937;
  font-size: 0.875rem;
  margin-bottom: 0.25rem;
  line-height: 1.3;
}

.notification-message {
  color: #4b5563;
  font-size: 0.8rem;
  line-height: 1.3;
  margin-bottom: 0.25rem;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.notification-time {
  color: #9ca3af;
  font-size: 0.75rem;
}

.notification-actions {
  margin-left: 0.5rem;
  margin-top: 0.25rem;
}

.empty-state {
  text-align: center;
  padding: 2rem 1rem;
  color: #9ca3af;
}

.empty-state i {
  font-size: 2rem;
  margin-bottom: 0.5rem;
  display: block;
}

.view-more {
  padding: 1rem;
  text-align: center;
  border-top: 1px solid #e5e7eb;
}

.dropdown-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 999;
  background: transparent;
}

/* Responsive */
@media (max-width: 768px) {
  .quick-notifications-dropdown {
    width: 320px;
    right: -120px;
  }
}

@media (max-width: 480px) {
  .quick-notifications-dropdown {
    width: 90vw;
    right: -150px;
  }
}
</style>